import { useState, useEffect, useRef } from 'react';
import type { MouseEvent } from 'react';
import styled, { keyframes, css, DefaultTheme } from 'styled-components';
import { HeartOutlineIc } from '@dsch/dd-icons';

import { GhostButton } from 'components/Toolkit/Button/GhostButton';
import { useOnUpdateOnly } from 'hooks/UseOnUpdateOnly';

type Variant = 'OVERLAY' | 'DEFAULT';

export interface SaveAdProps {
  isAdSaved?: boolean;
  toggleSave?: (event: MouseEvent) => void;
  isUnderlined?: boolean;
  hideText?: boolean;
  pulseOnLoad?: boolean;
  hasSavedAdAnimationOccurred?: boolean;
  setHasSavedAdAnimationOccurred?: (t: boolean) => void;
  variant?: Variant;
  className?: string;
}

const saveAnimation = (theme: DefaultTheme, variant: Variant) => keyframes`
  0% {
    transform: scale(1.1);
    fill: ${variant === 'OVERLAY' ? `${theme.colors.BLACK}3D` : 'transparent'};
    stroke: ${theme.colors.RED};
  }
  50% {
    transform: scale(0.7);
    stroke: ${theme.colors.RED};
  }
  100% {
    transform: scale(1);
    fill: ${theme.colors.RED_LIGHT};
    stroke: ${theme.colors.RED_LIGHT};
  };
`;

const unsaveAnimation = (theme: DefaultTheme, variant: Variant) => keyframes`
  0% {
    transform: scale(1);
    fill: ${theme.colors.RED_LIGHT};
    stroke: ${theme.colors.RED_LIGHT};
    }
  100% {
    transform: scale(1);
    fill: ${variant === 'OVERLAY' ? `${theme.colors.BLACK}3D` : 'transparent'};
    stroke: ${theme.colors.GREY_DARK};
  };
`;

const pulseAnimation = (theme: DefaultTheme, variant: Variant) => keyframes`
  0% {
    transform: scale(1);
    stroke: ${
      variant === 'OVERLAY' ? theme.colors.WHITE : theme.colors.GREY_DARK
    };
  }
  25% {
    transform: scale(0.7);
    stroke: ${theme.colors.RED};
  }
  40% {
    transform: scale(1.05);
  }
  50% {
    transform: scale(1);
    stroke: ${theme.colors.RED};
  }
  60% {
    transform: scale(1.05);
  }
  75% {
    transform: scale(0.7);
    stroke: ${theme.colors.RED};
  }
  90% {
    transform: scale(1);
  }
  100% {
    stroke: ${
      variant === 'OVERLAY' ? theme.colors.WHITE : theme.colors.GREY_DARK
    };
  };
`;

type TAction = {
  isUnderlined?: boolean;
  isAdSaved?: boolean;
  saved?: boolean;
  pulse: boolean;
  variant: Variant;
  hideText?: boolean;
};
const Action = styled(GhostButton)<TAction>`
  ${({ hideText }) =>
    !hideText &&
    `
    position: relative;
    width: 24px;
    
    & > svg {
      position: absolute;
      left: 0;
    }

    @media screen and (min-width: 40em) and (min-height: 768px) {
      width: 65px;
    }
  `}

  span {
    display: none;
  }

  & > svg > path {
    fill: ${({ theme, isAdSaved, variant }) =>
      isAdSaved
        ? theme.colors.RED_LIGHT
        : variant === 'OVERLAY'
        ? `${theme.colors.BLACK}3D`
        : 'transparent'};
    stroke: ${({ theme, isAdSaved, variant }) =>
      isAdSaved
        ? theme.colors.RED_LIGHT
        : variant === 'OVERLAY'
        ? theme.colors.WHITE
        : theme.colors.GREY_DARK};
    stroke-width: 1.6px;
    transition: all 200ms ease-in-out;
    transform-origin: 50%;

    animation: ${({ theme, pulse, saved, variant }) =>
      pulse
        ? css`
            ${pulseAnimation(theme, variant)} 2000ms ease-in-out forwards;
          `
        : saved
        ? css`
            ${saveAnimation(theme, variant)} 700ms ease-in-out forwards;
          `
        : saved === false
        ? css`
            ${unsaveAnimation(theme, variant)} 400ms ease-in-out;
          `
        : 'none'};
  }

  @media (hover: hover) {
    &:hover > svg > path {
      ${({ theme, isAdSaved }) =>
        !isAdSaved &&
        `
      stroke: ${theme.colors.RED};
      transform: scale(1.1);
    `};
    }
  }

  @media screen and (min-width: 40em) and (min-height: 768px) {
    span {
      display: inline;
    }
  }
`;

const Text = styled.span`
  position: absolute;
  left: 24px;
  ${({ theme }) => theme.fontSize.M14}
`;

function SaveAd({
  isAdSaved,
  toggleSave,
  isUnderlined,
  hideText,
  pulseOnLoad,
  hasSavedAdAnimationOccurred,
  setHasSavedAdAnimationOccurred,
  variant = 'DEFAULT',
  className,
}: SaveAdProps) {
  const [pulse, setPulse] = useState<boolean>(false);
  const [saved, setSaved] = useState<boolean>();
  const latestSave = useRef(isAdSaved);
  let timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useOnUpdateOnly(() => {
    setSaved(isAdSaved);
  }, [isAdSaved]);

  useEffect(() => {
    latestSave.current = isAdSaved;
    if (hasSavedAdAnimationOccurred === false) {
      if (pulseOnLoad) {
        timer.current = setTimeout(
          () => !latestSave.current && setPulse(true),
          5000,
        );
      }
    }

    return () => {
      timer.current && clearTimeout(timer.current);
    };
  }, [pulseOnLoad, isAdSaved, pulse, hasSavedAdAnimationOccurred]);

  function handleClick(event: MouseEvent) {
    setPulse(false);
    if (setHasSavedAdAnimationOccurred) {
      setHasSavedAdAnimationOccurred(true);
    }
    toggleSave && toggleSave(event);
  }

  return (
    <Action
      onClick={handleClick}
      data-testid="save-ad"
      data-tracking="ad-save-this-ad"
      isUnderlined={isUnderlined}
      isAdSaved={isAdSaved}
      saved={saved}
      pulse={pulse}
      className={className}
      variant={variant}
      hideText={hideText}
    >
      <HeartOutlineIc
        data-testid="save"
        onAnimationEnd={() => {
          if (pulse && setHasSavedAdAnimationOccurred) {
            setPulse(false);
            setHasSavedAdAnimationOccurred(true);
          }
        }}
      />
      {!hideText && <Text>{isAdSaved ? 'Saved' : 'Save'}</Text>}
    </Action>
  );
}

export { SaveAd };
